/**
 * Bulk Rescan Button Component
 * Shows a button to rescan all filtered places with progress tracking
 */

import { useState } from 'react';
import { Button, Progress, Space, Typography, Tooltip, Modal, Popconfirm } from 'antd';
import { SyncOutlined, StopOutlined, CheckCircleOutlined } from '@ant-design/icons';
import { useBulkRescan, type BulkRescanProgress } from '@/hooks/useBulkRescan';
import type { PlaceListItem } from '@/types/place';

const { Text } = Typography;

interface BulkRescanButtonProps {
  /** Function to fetch all places to rescan (with current filters) */
  fetchPlaces: () => Promise<PlaceListItem[]>;
  /** Whether places are currently loading */
  loading?: boolean;
  /** Whether the button should be disabled */
  disabled?: boolean;
  /** Total count of places that will be rescanned */
  totalCount?: number;
  /** Number of selected items (0 means rescan all) */
  selectedCount?: number;
}

export function BulkRescanButton({
  fetchPlaces,
  loading,
  disabled,
  totalCount = 0,
  selectedCount = 0,
}: BulkRescanButtonProps) {
  const { progress, startBulkRescan, cancelBulkRescan, resetProgress } = useBulkRescan();
  const [isModalVisible, setIsModalVisible] = useState(false);

  const handleStart = async () => {
    setIsModalVisible(true);
    const places = await fetchPlaces();
    await startBulkRescan(places);
  };

  const handleCancel = () => {
    cancelBulkRescan();
  };

  const handleClose = () => {
    if (!progress.isRunning) {
      setIsModalVisible(false);
      resetProgress();
    }
  };

  const percentComplete = progress.total > 0
    ? Math.round((progress.completed / progress.total) * 100)
    : 0;

  const getStatusText = () => {
    if (progress.isCancelled) {
      return 'Cancelled';
    }
    if (!progress.isRunning && progress.completed === progress.total && progress.total > 0) {
      return 'Complete';
    }
    if (progress.isRunning && progress.currentPlace) {
      return `Rescanning: ${progress.currentPlace.name}`;
    }
    return 'Starting...';
  };

  const buttonLabel = selectedCount > 0 ? `Rescan (${selectedCount})` : 'Rescan';
  const tooltipText = selectedCount > 0
    ? `Rescan ${selectedCount} selected places to refresh their data`
    : `Rescan all ${totalCount} filtered places to refresh their data`;

  return (
    <>
      <Tooltip title={tooltipText}>
        <Button
          icon={<SyncOutlined />}
          onClick={handleStart}
          loading={loading}
          disabled={disabled || totalCount === 0 || progress.isRunning}
        >
          {buttonLabel}
        </Button>
      </Tooltip>

      <Modal
        title={
          <Space>
            <SyncOutlined spin={progress.isRunning} />
            <span>Bulk Rescan Progress</span>
          </Space>
        }
        open={isModalVisible}
        onCancel={handleClose}
        footer={
          progress.isRunning ? (
            <Popconfirm
              title="Cancel rescan?"
              description="This will stop the rescan process. Already rescanned places will keep their updated data."
              onConfirm={handleCancel}
              okText="Yes, cancel"
              cancelText="No, continue"
            >
              <Button danger icon={<StopOutlined />}>
                Cancel Rescan
              </Button>
            </Popconfirm>
          ) : (
            <Button type="primary" onClick={handleClose} icon={<CheckCircleOutlined />}>
              Close
            </Button>
          )
        }
        closable={!progress.isRunning}
        maskClosable={!progress.isRunning}
        width={500}
      >
        <div style={{ padding: '16px 0' }}>
          {/* Progress bar */}
          <Progress
            percent={percentComplete}
            status={
              progress.isCancelled
                ? 'exception'
                : progress.isRunning
                ? 'active'
                : progress.failed > 0
                ? 'exception'
                : 'success'
            }
            strokeColor={progress.isCancelled ? '#faad14' : undefined}
          />

          {/* Status text */}
          <div style={{ marginTop: 16, marginBottom: 8 }}>
            <Text
              type={progress.isCancelled ? 'warning' : progress.isRunning ? undefined : 'success'}
              ellipsis
              style={{ display: 'block', maxWidth: '100%' }}
            >
              {getStatusText()}
            </Text>
          </div>

          {/* Stats */}
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              padding: '12px 16px',
              background: 'var(--ant-color-bg-layout)',
              borderRadius: 8,
              marginTop: 16,
            }}
          >
            <div style={{ textAlign: 'center' }}>
              <Text type="secondary" style={{ fontSize: 12, display: 'block' }}>
                Progress
              </Text>
              <Text strong>
                {progress.completed} / {progress.total}
              </Text>
            </div>
            <div style={{ textAlign: 'center' }}>
              <Text type="secondary" style={{ fontSize: 12, display: 'block' }}>
                Successful
              </Text>
              <Text strong style={{ color: 'var(--ant-color-success)' }}>
                {progress.successful}
              </Text>
            </div>
            <div style={{ textAlign: 'center' }}>
              <Text type="secondary" style={{ fontSize: 12, display: 'block' }}>
                Failed
              </Text>
              <Text strong style={{ color: progress.failed > 0 ? 'var(--ant-color-error)' : undefined }}>
                {progress.failed}
              </Text>
            </div>
          </div>

          {/* Estimated time remaining */}
          {progress.isRunning && progress.completed > 0 && (
            <div style={{ marginTop: 12, textAlign: 'center' }}>
              <Text type="secondary" style={{ fontSize: 12 }}>
                Estimated time remaining: ~
                {Math.ceil(((progress.total - progress.completed) * 12) / 60)} min
              </Text>
            </div>
          )}
        </div>
      </Modal>
    </>
  );
}

export default BulkRescanButton;
